import { Link } from "react-router-dom";
import { formatDistanceToNowStrict, isPast } from "date-fns";
import { Badge } from '@clearpath/design-system/ui/badge';
import { OriginTenantBadge } from "./OriginTenantBadge";
import { caseContext, statusLabel, statusTone } from "../lib/federation-format";
import type {
  FederationInboxOrderFragment,
  FederationInboxRowFragment,
} from "../__generated__/graphql";

export interface FederationInboxRowProps {
  /** Parent order (reliably populated) — source of barcode + case context. */
  order: FederationInboxOrderFragment;
  /** The EXTERNAL_VENDOR step run this row represents. */
  step: FederationInboxRowFragment;
}

/**
 * Single row in the federation inbox table.
 * Columns: origin · order & case · work type · SLA · status.
 *
 * `order` is the parent order node from the FederationInbox query (its
 * barcode + customer + line items are always present), rather than the
 * step run's nested `order` resolver which can come back null.
 */
export function FederationInboxRow({ order, step }: FederationInboxRowProps) {
  const link = step.vendorFederationLink;
  const clinic = order.customerParty?.name ?? null;
  const barcode = order.barcodeValue;
  const ctx = caseContext(order);

  const sla = order.estimatedCompletionAt;
  const overdue = sla ? isPast(new Date(sla)) : false;
  const workType = step.stepDefinition?.name ?? step.stepDefinition?.stepType ?? "—";

  return (
    <tr>
      <td>
        {link ? (
          <OriginTenantBadge
            partnerTenantId={link.partnerTenantId}
            label={link.label}
            partnerTenantName={link.partnerTenantName}
          />
        ) : (
          <span
            className="origin-tenant-badge origin-unknown"
            title="Originating tenant not resolved by the federation API"
          >
            Unknown origin
          </span>
        )}
        {clinic ? <div className="cell-sub">{clinic}</div> : null}
      </td>

      <td>
        <Link className="order-ref" to={`/orders/${order.id}`} title={order.id}>
          {barcode}
        </Link>
        {(ctx.restoration || ctx.teeth.length > 0 || ctx.shade || ctx.material) && (
          <div className="case-chips">
            {ctx.restoration && <span className="chip chip-restoration">{ctx.restoration}</span>}
            {ctx.teeth.length > 0 && (
              <span className="chip chip-teeth" title={`Teeth: ${ctx.teeth.join(", ")}`}>
                {ctx.teeth.length === 1
                  ? `#${ctx.teeth[0]}`
                  : `${ctx.teeth.length} teeth · ${ctx.teeth
                      .map((t) => `#${t}`)
                      .join(" ")}`}
              </span>
            )}
            {ctx.material && <span className="chip">{ctx.material}</span>}
            {ctx.shade && <span className="chip">shade {ctx.shade}</span>}
          </div>
        )}
      </td>

      <td>{workType}</td>

      <td className={`sla${overdue ? " overdue" : ""}`}>
        {sla ? (
          <time dateTime={sla}>
            {overdue
              ? `overdue ${formatDistanceToNowStrict(new Date(sla))}`
              : `in ${formatDistanceToNowStrict(new Date(sla))}`}
          </time>
        ) : (
          <span className="cell-empty">No SLA set</span>
        )}
      </td>

      <td>
        <Badge variant={statusTone(step.status)}>{statusLabel(step.status)}</Badge>
      </td>
    </tr>
  );
}
